/* eslint-disable react/forbid-prop-types,import/prefer-default-export,react/jsx-props-no-spreading,react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import BigNumber from 'bignumber.js';
import PropTypes from 'prop-types';
import {
  RATES_QUERY_KEY,
  SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  USER_PREFERENCES,
} from '../../../../../utilities/sharedQueries';
import { useAccountRestriction } from '../../../../../utilities/sharedHooks/account';
import { useLedgerFilteredCurrencies } from '../../../../../utilities/sharedHooks/ledgerWallets';
import {
  currencies,
  exchange as exchangeConfig,
  exchange,
} from '../../../../../utilities/constants';
import { formatNumber } from '../../../../../utilities/formatHelpers';
import { alphaSort, dynamicAssets } from '../../../../../utilities/helpers';
import { Wallet } from '../Wallet';
import { AccountItemPlaceholder } from '../../../../../components/Placeholders';
import { useUserPersonalInfo } from '../../../../../utilities/sharedHooks/user';

export const WalletsList = ({
  wallets,
  ledgerAccounts,
  isWalletsLoading,
  onDepositClick,
  onWithdrawalClick,
  onSendClick,
  selectedWalletType,
  onExchangeClick,
  showEmptyAccounts = false,
  showNoSearchResultFound = false,
}) => {
  const { t: translate } = useTranslation();
  const [sortedWallets, setSortedWallets] = useState([]);
  const [sortingOptions, setSortingOptions] = useState({
    ascending: false,
    columnKey: 'coin',
    castToNumber: true,
  });
  const { data: userPref } = useQuery(USER_PREFERENCES);
  const { data: rates } = useQuery(RATES_QUERY_KEY);
  const displayCurrency = userPref?.displayCurrency || 'EUR';

  const { restrictions } = useAccountRestriction();

  const { isUS } = useUserPersonalInfo();
  const isDepositHidden = process.env.REACT_APP_DEPOSIT_BUTTON_HIDDEN && !isUS;

  const { data: supportedCurrencies } = useLedgerFilteredCurrencies([
    SETTINGS_SUPPORTED_CURRENCIES_QUERY_KEY,
  ]);

  const handleSortWallets = (columnKey, castToNumber = true) => {
    setSortingOptions((prev) => ({
      columnKey,
      castToNumber,
      ascending: !prev.ascending,
    }));
  };

  const renderedWallets = useMemo(() => {
    const _renderedWallets = [];
    wallets.forEach((wallet) => {
      if (wallet && wallet.coin) {
        const _source = wallet.coin.toUpperCase();
        const _destination = displayCurrency.toUpperCase();
        let _rate = 0;
        let _changes = 0;
        if (rates) {
          if (_source === _destination) {
            _rate = 1;
            _changes = 0;
          } else if (rates[`${_source}${_destination}`]) {
            _rate = new BigNumber(rates[`${_source}${_destination}`].ask);
            const pctChange =
              rates[`${_source}${_destination}`].pctChange1d ||
              rates[`${_source}${_destination}`].pctChange24h;
            _changes = new BigNumber(pctChange);
          } else if (rates[`${_destination}${_source}`]) {
            _rate = new BigNumber(1).dividedBy(rates[`${_destination}${_source}`].ask);
            _changes = 0;
          }
        }

        const currency = currencies[wallet.coin];
        const isRateLessThanOne = parseFloat(_rate) <= 1;
        wallet.rate = formatNumber(
          _rate,
          isRateLessThanOne ? exchange.FIAT_LESS_ONE_VALUE : currencies[_destination].decimal
        );
        wallet.change = formatNumber(_changes, exchangeConfig.FIAT_VALUE);
        const walletBalance = parseFloat(_rate) * wallet.available_balance;
        wallet.fiat_balance = parseFloat(
          formatNumber(
            walletBalance * currency.conversion,
            isRateLessThanOne ? exchange.FIAT_LESS_ONE_VALUE : exchange.FIAT_VALUE
          )
        );
        _renderedWallets.push(wallet);
      }
    });
    return _renderedWallets;
  }, [displayCurrency, rates, wallets]);

  useEffect(() => {
    const comparerFn = (a, b) =>
      alphaSort(
        a,
        b,
        sortingOptions.columnKey,
        sortingOptions.ascending,
        sortingOptions.castToNumber
      );
    const _sortedWallets = renderedWallets.sort(comparerFn);
    setSortedWallets([..._sortedWallets]);
  }, [sortingOptions, renderedWallets]);

  const sortLabels = useMemo(
    () => [
      {
        id: 0,
        title: translate('coin_name_label'),
        withIcon: true,
        onClick: () => handleSortWallets('coin', false),
        classes: 'col-3',
      },
      {
        id: 1,
        title: translate('available_balance_label'),
        onClick: () => handleSortWallets('fiat_balance', true),
        withIcon: true,
        classes: 'col-2',
      },
      selectedWalletType === 'crypto'
        ? {
            id: 2,
            title: translate('price_label'),
            onClick: () => handleSortWallets('rate', true),
            withIcon: true,
            classes: 'd-none d-md-block col-2',
          }
        : false,
      {
        id: 4,
        title: translate('actions_label'),
        withIcon: false,
        isRight: true,
        classes: 'col-4',
      },
    ],
    [selectedWalletType]
  );

  const renderSortLabels = () => (
    <div className="d-none d-md-block container-fluid p-0 mt-4">
      <div className="row w-100 m-auto">
        {sortLabels.map((item, index) => {
          const attribs = {};
          if (item.withIcon) {
            attribs.tabIndex = '0';
            attribs.role = 'button';
            attribs.onClick = item.onClick;
            attribs.onKeyDown = () => {};
          }

          return (
            <div
              className={`col ${index === 0 ? 'ps-0' : ''} ${
                index === sortLabels.length - 1 ? 'pe-0' : ''
              }`}
              key={item.id}>
              <span
                key={item.id}
                style={item.isRight ? { marginLeft: 'auto' } : {}}
                className={`text-shadow text-f6 text-start text-nowrap d-flex flex-even align-items-center ${
                  item.isRight ? 'justify-content-end' : ''
                } ${item.classes}`}
                {...attribs}>
                <span>
                  {item.withIcon ? (
                    <img
                      alt="sort"
                      src="./assets/images/sort.svg"
                      className="me-1"
                      style={{ width: '1rem', height: '1rem' }}
                    />
                  ) : null}
                  <span>{item.title}</span>
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  const ledgerAccountWallet = useCallback(
    (wallet) => {
      if (ledgerAccounts && sortedWallets) {
        if (
          supportedCurrencies &&
          supportedCurrencies[wallet?.coin] &&
          supportedCurrencies[wallet?.coin].is_erc20_token &&
          ledgerAccounts[currencies.ETH.icon.toLowerCase()]
        ) {
          return ledgerAccounts[currencies.ETH.icon.toLowerCase()];
        }
        return ledgerAccounts[wallet?.coin];
      }
      return null;
    },
    [ledgerAccounts, sortedWallets]
  );

  const renderWallets = () => (
    <>
      {sortedWallets.map((wallet, index) => (
        <Wallet
          key={`${wallet.id}-${wallet.coin}`}
          isDepositHidden={isDepositHidden && !['usdc', 'euroc'].includes(wallet?.coin)}
          isReceiveDisabled={restrictions[wallet?.coin]?.receiveDisabled}
          isSendDisabled={restrictions[wallet?.coin]?.sendDisabled}
          isExchangeDisabled={restrictions[wallet?.coin]?.exchangeDisabled}
          ledgerAccount={ledgerAccountWallet(wallet)}
          onWithdrawalClick={() => onWithdrawalClick(wallet)}
          onDepositClick={() => onDepositClick(wallet)}
          onSendClick={() => onSendClick(wallet)}
          onExchangeClick={() => onExchangeClick(wallet)}
          wallet={wallet}
          selectedWalletType={selectedWalletType}
          className={`${index !== 0 ? 'mt-2' : ''} ${
            index !== sortedWallets.length - 1 ? 'mb-2' : ''
          }`}
        />
      ))}
    </>
  );

  if ((showNoSearchResultFound || showEmptyAccounts) && !isWalletsLoading) {
    return (
      <div className="w-100 h-65vh d-flex flex-row justify-content-center aligh-items-center wallets-container">
        {showEmptyAccounts && (
          <div
            className={`text-center wallets-container-empty ${
              selectedWalletType === 'crypto' ? 'cryptoWidth' : 'fiatWidth'
            }`}>
            <img
              alt="No wallets"
              src={dynamicAssets(
                `./assets/images/${
                  selectedWalletType === 'crypto' ? 'crypto-wallets.svg' : 'fiat_Wallet.svg'
                }`
              )}
            />
            <h2 className="mt-4 mb-3">{translate('no_accounts_label')}</h2>
            <p style={{ whiteSpace: 'pre-line' }} className="text-shadow">
              {translate(
                selectedWalletType === 'crypto'
                  ? 'no_accounts_sublabel'
                  : 'no_accounts_sublabel_fiat'
              )}
            </p>
          </div>
        )}
        {showNoSearchResultFound && !showEmptyAccounts && (
          <div
            className={`text-center wallets-container-empty  ${
              selectedWalletType === 'crypto' ? 'cryptoWidth' : 'fiatWidth'
            }`}>
            <img alt="No results" src={dynamicAssets('./assets/images/no-deposit.png')} />
            <h2 className="mt-4 mb-3">{translate('search_no_results_found_label')}</h2>
            <p style={{ whiteSpace: 'pre-line' }} className="text-shadow">
              {translate('search_try_different_criteria_label')}
            </p>
          </div>
        )}
      </div>
    );
  }

  return (
    <>
      {renderSortLabels()}
      <div className="wallets-container-list">
        {!isWalletsLoading && renderWallets()}
        {isWalletsLoading && (
          <div className="mt-3">
            <AccountItemPlaceholder />
            <AccountItemPlaceholder />
            <AccountItemPlaceholder />
            <AccountItemPlaceholder />
          </div>
        )}
      </div>
    </>
  );
};

WalletsList.propTypes = {
  wallets: PropTypes.array.isRequired,
  ledgerAccounts: PropTypes.object.isRequired,
  isWalletsLoading: PropTypes.bool.isRequired,
  onDepositClick: PropTypes.func.isRequired,
  onWithdrawalClick: PropTypes.func.isRequired,
  refetchWallets: PropTypes.func.isRequired,
  onSendClick: PropTypes.func.isRequired,
  selectedWalletType: PropTypes.string.isRequired,
  onExchangeClick: PropTypes.func.isRequired,
  userRegionSettings: PropTypes.object.isRequired,
  showEmptyAccounts: PropTypes.bool,
  showNoSearchResultFound: PropTypes.bool,
};
